// bureau/src/pages/UserCreation/AddProfileViaText.tsx

import React, { ChangeEvent, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaArrowLeft, FaMagic } from "react-icons/fa";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { AiOutlineClose } from "react-icons/ai";
import ProfileForm from "./components/ProfileViaForm";
import { toast } from "react-toastify";
import { normalizeProfileData } from "./helpers/creation.helper";
import { useLanguage } from "../../context/LanguageContext";

interface ProfileData {
  gender: string;
  createdBy: string;
  citizenship: string;
  mobileNumber: string;
  password: string;
  fullName: string;
  dateOfBirth: string;
  birthTime: string;
  maritalStatus: string;
  height: string;
  religion: string;
  caste: string;
  subCaste: string;
  education: string;
  employment: string;
  occupation: string;
  annualIncome: string;
  fatherOccupation: string;
  motherOccupation: string;
  brothers: string;
  sisters: string;
  totalPropertyValue: string;
  profileDescription: string;
  raasi: string;
  nakshatram: string;
  gothram: string;
  placeOfBirth: string;
  country: string;
  state: string;
  district: string; // 👈 REQUIRED
}

const AddProfileViaText: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const bureauInfo = useSelector((state: RootState) => state.bureau.data);
  const isMediatorProfile = location.state?.isMediatorProfile || false;
  const [inputText, setInputText] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const { t } = useLanguage();
  const bureauId = bureauInfo?.bureauId;
  if (!bureauId) {
    return <div>{t("Invalid bureau session. Please re-login.")}</div>;
  }

  const [profileData, setProfileData] = useState<ProfileData>({
    gender: "",
    createdBy: "self",
    citizenship: "Indian",
    mobileNumber: "",
    password: "",
    fullName: "",
    dateOfBirth: "",
    birthTime: "",
    maritalStatus: "",
    height: "",
    religion: "",
    caste: "",
    subCaste: "",
    education: "",
    employment: "",
    occupation: "",
    annualIncome: "",
    fatherOccupation: "",
    motherOccupation: "",
    brothers: "",
    sisters: "",
    totalPropertyValue: "",
    country: "",
    state: "",
    district: "",
    profileDescription: "",
    raasi: "",
    nakshatram: "",
    gothram: "",
    placeOfBirth: "",
  });
  type ModalData = {
    key: keyof ProfileData;
    options: { value: any; label: string }[];
  };

  const normalizeMaritalStatus = (value: string) => {
    const v = value.toLowerCase();
    if (v.includes("never") || v.includes("unmarried")) return "neverMarried";
    if (v.includes("waiting")) return "awaitingDivorce";
    if (v.includes("divorce")) return "divorced";
    if (v.includes("widow")) return "widow";
    return "neverMarried";
  };

  const normalizeReligion = (value: string) => {
    const v = value.toLowerCase();
    if (v.includes("hindu")) return "hindu";
    if (v.includes("muslim")) return "muslim";
    if (v.includes("christian")) return "christian";
    if (v.includes("sikh")) return "sikh";
    if (v.includes("buddhist")) return "buddhist";
    return "";
  };

  const generatePassword = () => {
    const digits = "0123456789";
    let password = "";

    for (let i = 0; i < 8; i++) {
      password += digits.charAt(Math.floor(Math.random() * digits.length));
    }

    return password;
  };

  const extractDataFromText = (text: string): ProfileData => {
    const lines = (text || "")
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean);

    const data = {} as Partial<ProfileData>;

    // ---------- helpers ----------
    const extractValue = (line: string, keywords: string[] = []): string => {
      let cleaned = (line || "")
        .replace(/^[\s•\-\u2022]+/g, "") // bullets remove
        .trim();

      // normalize weird separators
      cleaned = cleaned.replace(/\s{2,}/g, " ");

      // try : or - or =
      const match = cleaned.match(/[:：=\-]\s*(.+)/);
      if (match?.[1]) return match[1].trim();

      // try "Keyword  value" (no colon)
      for (const k of keywords) {
        const re = new RegExp(`^${k}\\s+(.+)$`, "i");
        const m2 = cleaned.match(re);
        if (m2?.[1]) return m2[1].trim();
      }

      // try "Keyword\tvalue"
      for (const k of keywords) {
        const re = new RegExp(`^${k}\\s*\\t\\s*(.+)$`, "i");
        const m3 = cleaned.match(re);
        if (m3?.[1]) return m3[1].trim();
      }

      return "";
    };

    const findLineWithKeywords = (keywords: string[]): string => {
      for (const line of lines) {
        const lower = line.toLowerCase();
        if (keywords.some((k) => lower.includes(k.toLowerCase()))) {
          return extractValue(line, keywords);
        }
      }
      return "";
    };

    const findRawLineWithKeywords = (keywords: string[]): string => {
      for (const line of lines) {
        const lower = line.toLowerCase();
        if (keywords.some((k) => lower.includes(k.toLowerCase()))) {
          return line;
        }
      }
      return "";
    };

    const normalizeHeight = (raw?: string): string => {
      if (!raw) return "";
      const s = raw.replace(/\s+/g, " ").trim();

      // 5.4 (treat as feet.decimal -> convert to inches)
      const decimal = s.match(/^(\d)\.(\d)$/);
      if (decimal) {
        const ft = Number(decimal[1]);
        const dec = Number(`0.${decimal[2]}`); // .4
        const inches = Math.round(dec * 12); // .4 * 12 = 4.8 -> 5
        return `${ft}'${inches}"`;
      }

      // existing cases...
      const feetInch = s.match(/(\d)\s*[’']\s*(\d{1,2})/);
      if (feetInch) return `${feetInch[1]}'${feetInch[2]}"`;

      const ftIn = s.match(
        /(\d+)\s*(feet|foot|ft)\s*(\d+)\s*(inches|inch|in)/i,
      );
      if (ftIn) return `${ftIn[1]}'${ftIn[3]}"`;

      const onlyFt = s.match(/(\d+(\.\d+)?)\s*(ft|feet|foot)/i);
      if (onlyFt) return `${onlyFt[1]} ft`;

      return s;
    };

    const normalizeIncome = (raw?: string): string => {
      if (!raw) return "";
      return raw.replace(/\s+/g, " ").trim();
    };

    const normalizePlace = (raw?: string): string => {
      if (!raw) return "";
      return raw.replace(/\s+/g, " ").trim();
    };

    const parseDob = (value: string): string => {
      if (!value) return "";

      // matches dd-mm-yyyy / dd/mm/yyyy / dd.mm.yyyy
      const m = value.match(/(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{4})/);
      if (m) {
        const dd = m[1].padStart(2, "0");
        const mm = m[2].padStart(2, "0");
        const yyyy = m[3];
        return `${yyyy}-${mm}-${dd}`;
      }

      // sometimes yyyy-mm-dd already
      const m2 = value.match(/(\d{4})[\/\-\.](\d{1,2})[\/\-\.](\d{1,2})/);
      if (m2) {
        const yyyy = m2[1];
        const mm = m2[2].padStart(2, "0");
        const dd = m2[3].padStart(2, "0");
        return `${yyyy}-${mm}-${dd}`;
      }

      return "";
    };

    const normalizeGender = (value: string) => {
      const v = (value || "").toLowerCase();
      if (
        v.includes("male") ||
        v === "m" ||
        v.includes("groom") ||
        v.includes("boy") ||
        v.includes("వరుడు") ||
        v.includes("మగ") ||
        v.includes("పురుష")
      ) {
        return "male";
      }
      if (
        v.includes("female") ||
        v === "f" ||
        v.includes("bride") ||
        v.includes("girl") ||
        v.includes("వధువు") ||
        v.includes("ఆడ") ||
        v.includes("స్త్రీ")
      ) {
        return "female";
      }
      return "";
    };

    const parseTimeFromText = (value?: string): string => {
      if (!value) return "";
      // e.g. 8:14 am, 9:54 AM, 10 AM
      const m =
        value.match(/\b(\d{1,2}:\d{2}\s*(am|pm))\b/i) ||
        value.match(/\b(\d{1,2}\s*(am|pm))\b/i);
      return m?.[1] ? m[1].toUpperCase().replace(/\s+/g, " ") : value.trim();
    };

    const parseSiblingsCounts = (raw?: string) => {
      if (!raw) return { brothers: "", sisters: "" };
      const s = raw.toLowerCase();
      const bro = s.match(/(\d+)\s*brother/);
      const sis = s.match(/(\d+)\s*sister/);
      return {
        brothers: bro?.[1] ?? "",
        sisters: sis?.[1] ?? "",
      };
    };

    // ---------- extraction ----------
    // Name
    data.fullName =
      findLineWithKeywords(["full name", "name", "surname"]) ||
      findLineWithKeywords(["name"]);

    // DOB
    const dobLine = findLineWithKeywords([
      "date of birth",
      "dob",
      "birth date",
    ]);
    if (dobLine) data.dateOfBirth = parseDob(dobLine);

    // Birth time (direct)
    const birthTimeLine = findLineWithKeywords([
      "time of birth",
      "birth time",
      "born time",
    ]);
    if (birthTimeLine) data.birthTime = parseTimeFromText(birthTimeLine);

    // Place of birth (direct)
    const pobLine = findLineWithKeywords(["place of birth", "birth place"]);
    if (pobLine) data.placeOfBirth = normalizePlace(pobLine);

    // "Born Time & place" combined line (split)
    const bornCombined = findLineWithKeywords([
      "born time & place",
      "born time and place",
      "born time",
      "time & place",
      "time and place",
    ]);
    if (bornCombined) {
      // example: "8:14 am,Vijayawada"
      const parts = bornCombined
        .split(",")
        .map((p) => p.trim())
        .filter(Boolean);

      if (!data.birthTime && parts[0])
        data.birthTime = parseTimeFromText(parts[0]);
      if (!data.placeOfBirth && parts[1])
        data.placeOfBirth = normalizePlace(parts[1]);
    }

    // Age if DOB missing
    if (!data.dateOfBirth) {
      const ageLine = findLineWithKeywords(["age", "years"]);
      if (ageLine) {
        const ageMatch = ageLine.match(/(\d{1,3})/);
        if (ageMatch) {
          const age = parseInt(ageMatch[1]);
          if (!Number.isNaN(age) && age > 0 && age < 120) {
            const currentYear = new Date().getFullYear();
            const birthYear = currentYear - age;
            data.dateOfBirth = `${birthYear}-01-01`;
          }
        }
      }
    }

    // Height
    const heightLine = findLineWithKeywords(["height", "ht"]);
    if (heightLine) data.height = normalizeHeight(heightLine);

    // Gender
    const genderLine = findLineWithKeywords([
      "gender",
      "sex",
      "male",
      "female",
      "bride",
      "groom",
      "మగ",
      "ఆడ",
      "వధువు",
      "వరుడు",
    ]);
    const genderFromText = normalizeGender(genderLine || text);
    if (genderFromText) data.gender = genderFromText;

    // Religion
    const religionLine = findLineWithKeywords([
      "religion",
      "relegion",
      "matham",
      "మతం",
    ]);
    if (religionLine) data.religion = normalizeReligion(religionLine);

    // Caste / Subcaste / Gothram
    data.caste = findLineWithKeywords(["caste", "cast", "కులం"]);

    // sub caste should NOT include gothram; keep separate
    data.subCaste = findLineWithKeywords([
      "sub caste",
      "subcast",
      "sub-caste",
      "sub cast",
      "sub cast:",
    ]);

    data.gothram = findLineWithKeywords([
      "gothram",
      "gotram",
      "gotra",
      "gotra:",
    ]);

    // Rashi / Star (fix: star goes to nakshatram)
    data.raasi = findLineWithKeywords(["raasi", "rasi", "rashi"]);
    data.nakshatram = findLineWithKeywords(["nakshatram", "nakshatra", "star"]);

    // Education
    data.education = findLineWithKeywords([
      "highest education",
      "education",
      "qualification",
      "educational qualification",
      "degree",
      "study",
    ]);

    // Occupation / Job description
    const occupationLine = findLineWithKeywords([
      "occupation",
      "job",
      "designation",
      "company",
      "business",
      "employee",
      "employed",
      "employment",
      "working",
    ]);

    if (occupationLine) {
      data.occupation = occupationLine;
    }

    // Income
    const salaryLine = findLineWithKeywords([
      "salary",
      "income",
      "package",
      "annual income",
      "monthly income",
    ]);
    if (salaryLine) data.annualIncome = normalizeIncome(salaryLine);

    // Father / Mother Occupation (support more patterns)
    data.fatherOccupation = findLineWithKeywords([
      "father occupation",
      "father's occupation",
      "father job",
      "father job description",
    ]);

    data.motherOccupation = findLineWithKeywords([
      "mother occupation",
      "mother's occupation",
      "mother job",
      "mother job description",
    ]);

    // Siblings / brothers / sisters (handles: "Siblings - 1 Brother...")
    const siblingsRaw =
      findLineWithKeywords(["siblings"]) ||
      findLineWithKeywords(["brothers"]) ||
      findLineWithKeywords(["sisters"]);

    if (siblingsRaw) {
      const counts = parseSiblingsCounts(siblingsRaw);
      if (counts.brothers) data.brothers = counts.brothers;
      if (counts.sisters) data.sisters = counts.sisters;
    } else {
      // fallback: old behavior
      const brothersLine = findLineWithKeywords(["brothers"]);
      if (brothersLine) data.brothers = brothersLine;

      const sistersLine = findLineWithKeywords(["sisters"]);
      if (sistersLine) data.sisters = sistersLine;
    }

    // Marital status
    const maritalLine = findLineWithKeywords([
      "marital status",
      "married",
      "unmarried",
      "never married",
      "divorce",
      "divorced",
      "widow",
      "widower",
      "awaiting",
      "second match",
      "first match",
    ]);
    data.maritalStatus = maritalLine
      ? normalizeMaritalStatus(maritalLine)
      : "neverMarried";

    // Mobile number (first 10-digit)
    const phoneRegex = /\b\d{10}\b/g;
    const phoneMatches = (text || "").match(phoneRegex);
    if (phoneMatches?.length) data.mobileNumber = phoneMatches[0];

    // Property
    data.totalPropertyValue = findLineWithKeywords([
      "total property value",
      "property value",
      "property",
      "assets",
    ]);

    // Password
    data.password = generatePassword();

    // Defaults
    data.createdBy = isMediatorProfile ? "other_mediator" : profileData.createdBy;
    data.citizenship = profileData.citizenship;

    data.country = findLineWithKeywords(["country"]);
    data.state = findLineWithKeywords(["state"]);
    data.district = findLineWithKeywords(["district", "city", "town", "place"]);

    const totalBro = findLineWithKeywords(["total brothers", "brothers total"]);
    const totalSis = findLineWithKeywords(["total sisters", "sisters total"]);

    if (totalBro) data.brothers = totalBro.match(/\d+/)?.[0] ?? totalBro;
    if (totalSis) data.sisters = totalSis.match(/\d+/)?.[0] ?? totalSis;

    const employmentLine = findLineWithKeywords([
      "employment",
      "employee",
      "employed",
      "working",
    ]);

    if (employmentLine) {
      data.employment = employmentLine; // "pravate employee"
    }

    return data as ProfileData;
  };

  const handleParseText = () => {
    if (!inputText.trim()) {
      toast.error("Please enter profile text to parse");
      return;
    }

    setIsProcessing(true);
    setTimeout(() => {
      const extractedData = normalizeProfileData(
        extractDataFromText(inputText),
      );
      setProfileData(extractedData as ProfileData);

      setIsProcessing(false);
      setIsEditing(true);
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center mb-6">
          <button
            onClick={() => navigate(-1)}
            className="mr-4 p-2 rounded-full hover:bg-white transition duration-300"
          >
            <FaArrowLeft className="text-xl text-gray-700" />
          </button>
          <h1 className="text-3xl font-bold text-gray-800">
            {t("Add")} {isMediatorProfile ? t("Mediator") : t("Own")}{" "}
            {t("Profile via Text")}
          </h1>
        </div>

        {!isEditing ? (
          /* Text Input Section */
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="mb-4">
              <label className="block text-gray-700 font-semibold mb-2">
                {t("Paste Profile Details")}
              </label>
              <textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder="Paste the profile details here...
                Example format:
                NAME: John Doe
                DATE OF BIRTH: 12/10/1990
                HEIGHT: 5 feet 8 inches
                CASTE: Kapu
                EDUCATION: B.Tech Computer Science
                OCCUPATION: Software Engineer
                SALARY: 12 Lakhs per annum
                ..."
                className="w-full h-96 p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              />
            </div>

            <button
              onClick={handleParseText}
              disabled={isProcessing}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-6 rounded-lg flex items-center justify-center hover:shadow-lg transition duration-300 disabled:opacity-50"
            >
              {isProcessing ? (
                <>
                  <div className="animate-spin mr-2 h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                  {t("Processing")}...
                </>
              ) : (
                <>
                  <FaMagic className="mr-2" />
                  {t("Parse & Extract Data")}
                </>
              )}
            </button>
          </div>
        ) : (
          /* Editable Form Section */
          <ProfileForm
            initialData={profileData}
            bureauId={bureauId}
            isMediatorProfile={isMediatorProfile}
            onBack={() => setIsEditing(false)}
            onSuccess={() => navigate(-1)}
          />
        )}
      </div>
    </div>
  );
};

export default AddProfileViaText;
